// frontend/src/features/ogdb2/components/ExportLeaseSummaryModal.jsx
import React, { useEffect, useMemo, useState } from "react";

function Spinner({ className = "" }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

export default function ExportLeaseSummaryModal({
  open,
  onClose,
  defaultFirmFormat, // "capitol" | "pritchard_abbott" | "wardlaw" | ""
  defaultOnlyBeneficial = true,
  defaultBeneficialMinThresholdPct = "", // string for input convenience
  onExport, // async ({ appraiser_format, only_beneficial_asks, beneficial_min_threshold_pct }) => void
}) {
  const [appraiserFormat, setAppraiserFormat] = useState(defaultFirmFormat || "capitol");
  const [onlyBeneficial, setOnlyBeneficial] = useState(!!defaultOnlyBeneficial);
  const [minPct, setMinPct] = useState(
    defaultBeneficialMinThresholdPct === null || defaultBeneficialMinThresholdPct === undefined
      ? ""
      : String(defaultBeneficialMinThresholdPct)
  );
  const [isExporting, setIsExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      setIsExporting(false);
      setExportErr("");
      setAppraiserFormat(defaultFirmFormat || "capitol");
      setOnlyBeneficial(!!defaultOnlyBeneficial);
      setMinPct(
        defaultBeneficialMinThresholdPct === null || defaultBeneficialMinThresholdPct === undefined
          ? ""
          : String(defaultBeneficialMinThresholdPct)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(
    () => [
      { value: "capitol", label: "Capitol Appraisal Group" },
      { value: "pritchard_abbott", label: "Pritchard & Abbott" },
      { value: "wardlaw", label: "Wardlaw Appraisal Group" },
    ],
    []
  );

  if (!open) return null;

  // ✅ Accept: "5", "5.0", "5%", " 5 % ", "2,500" (commas removed)
  function parseMinPct(raw) {
    let s = String(raw ?? "").trim();
    if (!s) return null;

    // remove commas and spaces
    s = s.replace(/,/g, "").replace(/\s+/g, "");

    // allow a trailing percent sign
    if (s.endsWith("%")) s = s.slice(0, -1);

    if (!s) return null;

    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return null;
    return n; // percentage points (e.g. 5 means 5%)
  }

  const parsedMinPct = parseMinPct(minPct);
  const invalidMinPct = onlyBeneficial && minPct.trim() !== "" && parsedMinPct === null;

  async function handleExportClick() {
    if (isExporting) return;
    setExportErr("");

    if (invalidMinPct) return;

    setIsExporting(true);
    try {
      // Ensure we treat onExport as async even if it sometimes returns void
      await Promise.resolve(
        onExport?.({
          appraiser_format: appraiserFormat,
          only_beneficial_asks: onlyBeneficial,
          beneficial_min_threshold_pct: onlyBeneficial ? parseMinPct(minPct) : null,
        })
      );
    } catch (e) {
      // Keep the modal open and show a friendly error
      const msg = e?.response?.data?.detail || e?.message || "Export failed";
      setExportErr(String(msg));
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">Export Lease Summary (Excel)</div>

          <button className="btn" type="button" onClick={onClose} disabled={isExporting}>
            Close
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">Appraiser format</div>
            <select
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm disabled:opacity-60"
              value={appraiserFormat}
              onChange={(e) => setAppraiserFormat(e.target.value)}
              disabled={isExporting}
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-500 mt-1">
              This controls the base column order (copied from your original import rows), plus BA Ask columns.
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={onlyBeneficial}
              onChange={(e) => setOnlyBeneficial(e.target.checked)}
              disabled={isExporting}
            />
            Only export beneficial asks
          </label>

          <div className="grid grid-cols-1 gap-2">
            <div className="text-xs font-semibold text-gray-600">Beneficial minimum threshold (%)</div>
            <input
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm disabled:opacity-60"
              placeholder="e.g. 5 or 5%"
              value={minPct}
              onChange={(e) => setMinPct(e.target.value)}
              disabled={!onlyBeneficial || isExporting}
            />
            <div className="text-xs text-gray-500">
              When enabled, an ask is exported only if it’s beneficial by at least this % vs the assessor baseline.
              Leave blank for the current behavior (any improvement).
            </div>
            {invalidMinPct ? (
              <div className="text-xs text-red-600">Enter a valid non-negative number (example: 2, 2.5, 5%).</div>
            ) : null}
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
            BA Ask columns will be highlighted yellow. Any cell containing an exported ask will also be highlighted.
            Rows with no asks are still included.
          </div>

          {exportErr ? <div className="text-xs text-red-600">{exportErr}</div> : null}

          {isExporting ? (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Spinner className="h-4 w-4" />
              <div>Exporting…</div>
            </div>
          ) : null}
        </div>

        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button className="btn" type="button" onClick={onClose} disabled={isExporting}>
            Cancel
          </button>

          <button
            className="btn btn-primary inline-flex items-center gap-2"
            type="button"
            onClick={handleExportClick}
            disabled={isExporting || invalidMinPct}
          >
            {isExporting ? (
              <>
                <Spinner className="h-4 w-4" />
                Exporting…
              </>
            ) : (
              "Export Excel"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}